import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  UploadSimple, Sparkle, ArrowRight, CheckCircle, WarningCircle,
  Files, ChatCircleText, Lightning, ArrowsClockwise, X, Check
} from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';

/**
 * Smart Import Wizard — destination locked to SalesCrunchCRM
 * Steps:
 *  1) Upload contacts file → AI maps fields
 *  2) Review/adjust mappings → preflight
 *  3) Confirm import
 *  4) (optional) Upload notes file → AI matches
 *  5) Review matches → import notes
 */

const STEPS = [
  { id: 1, label: 'Upload File' },
  { id: 2, label: 'Review Fields' },
  { id: 3, label: 'Pre-flight' },
  { id: 4, label: 'Import Notes' },
  { id: 5, label: 'Activity History' },
  { id: 6, label: 'Relationships' },
  { id: 7, label: 'Attachments' },
  { id: 8, label: 'Done' },
];

export default function SmartImportPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [session, setSession] = useState(null); // upload response
  const [mappings, setMappings] = useState({}); // {col: target}
  const [preflight, setPreflight] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const [notesSession, setNotesSession] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [acceptedNoteIds, setAcceptedNoteIds] = useState(new Set());
  const [notesImport, setNotesImport] = useState(null);

  const fileInput = useRef(null);
  const notesInput = useRef(null);

  const reset = () => {
    setStep(1); setSession(null); setMappings({}); setPreflight(null);
    setImportResult(null); setNotesSession(null); setMatchResult(null);
    setAcceptedNoteIds(new Set()); setNotesImport(null); setError('');
    setActivityFile(null); setActivityResult(null);
    setRelFile(null); setRelResult(null);
    setAttFile(null); setAttResult(null);
  };

  // ─── Step 1: Upload ────────────────────────────────────────────
  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true); setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/migrate/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSession(data);
      // Initialize mappings with AI suggestions
      const initial = {};
      Object.entries(data.ai_mappings || {}).forEach(([col, m]) => {
        initial[col] = m.target || 'skip';
      });
      // Any column without a suggestion → skip
      data.columns.forEach(c => { if (!initial[c]) initial[c] = 'skip'; });
      setMappings(initial);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  // ─── Step 2 → 3: Preflight ─────────────────────────────────────
  const runPreflight = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/migrate/${session.session_id}/preflight`, { mappings });
      setPreflight(data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Preflight failed');
    } finally { setBusy(false); }
  };

  // ─── Step 3: Confirm Import ────────────────────────────────────
  const runImport = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/migrate/${session.session_id}/import-contacts`, {
        skip_duplicates: true,
        default_status: 'lead',
        tag_for_batch: `import-${new Date().toISOString().substring(0, 10)}`,
      });
      setImportResult(data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  // ─── Step 4: Upload + Match Notes ──────────────────────────────
  const onNotesUpload = async (file) => {
    if (!file) return;
    setUploading(true); setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post(`/migrate/${session.session_id}/upload-notes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setNotesSession(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Notes upload failed');
    } finally { setUploading(false); }
  };

  const runMatch = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/migrate/${session.session_id}/match-notes`);
      setMatchResult(data);
      // Auto-accept high-confidence matches
      const high = new Set();
      data.matches.forEach(m => { if ((m.confidence || 0) >= 0.85 && m.contact_id) high.add(m.note_id); });
      setAcceptedNoteIds(high);
    } catch (err) {
      setError(err.response?.data?.detail || 'AI matching failed');
    } finally { setBusy(false); }
  };

  const importMatchedNotes = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/migrate/${session.session_id}/import-notes`, {
        accepted_note_ids: Array.from(acceptedNoteIds),
      });
      setNotesImport(data);
      setStep(5);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import notes failed');
    } finally { setBusy(false); }
  };

  const skipNotes = () => setStep(5);
  const skipActivities = () => setStep(6);
  const skipRelationships = () => setStep(7);
  const skipAttachments = () => setStep(8);

  // Step 5: Activity History import
  const [activityFile, setActivityFile] = useState(null);
  const [activityResult, setActivityResult] = useState(null);

  const importActivities = async () => {
    if (!activityFile || !session) return;
    setError(''); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', activityFile);
      const { data } = await api.post(`/migrate/${session.session_id}/import-activities`, fd);
      setActivityResult(data);
      setStep(6);
    } catch (err) {
      setError(err.response?.data?.detail || 'Activity import failed');
    } finally {
      setBusy(false);
    }
  };

  // Step 6: Relationships import
  const [relFile, setRelFile] = useState(null);
  const [relResult, setRelResult] = useState(null);

  const importRelationships = async () => {
    if (!relFile || !session) return;
    setError(''); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', relFile);
      const { data } = await api.post(`/migrate/${session.session_id}/import-relationships`, fd);
      setRelResult(data);
      setStep(7);
    } catch (err) {
      setError(err.response?.data?.detail || 'Relationship import failed');
    } finally {
      setBusy(false);
    }
  };

  // Step 7: Attachments metadata import
  const [attFile, setAttFile] = useState(null);
  const [attResult, setAttResult] = useState(null);

  const importAttachments = async () => {
    if (!attFile || !session) return;
    setError(''); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', attFile);
      const { data } = await api.post(`/migrate/${session.session_id}/import-attachments`, fd);
      setAttResult(data);
      setStep(8);
    } catch (err) {
      setError(err.response?.data?.detail || 'Attachments import failed');
    } finally {
      setBusy(false);
    }
  };

  // Import History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get('/migrate/');
      setHistory(data || []);
    } catch (err) {
      // ignore
    } finally {
      setHistoryLoading(false);
    }
  };
  const toggleAccept = (id) => {
    setAcceptedNoteIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // ─── UI helpers ────────────────────────────────────────────────
  const StepBar = () => (
    <div className="flex items-center gap-2 mb-6 px-1" data-testid="step-bar">
      {STEPS.map((s, i) => (
        <div key={s.id} className="flex items-center gap-2 flex-1">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
            step > s.id ? 'bg-green-500 text-white' :
            step === s.id ? 'bg-[#0B5394] text-white' : 'bg-[#E5E7EB] text-[#666]'
          }`}>
            {step > s.id ? <Check size={12} weight="bold" /> : s.id}
          </div>
          <span className={`text-[11px] font-medium whitespace-nowrap ${step >= s.id ? 'text-[#111]' : 'text-[#999]'}`}>{s.label}</span>
          {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${step > s.id ? 'bg-green-500' : 'bg-[#E5E7EB]'}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-[#E8E8E8]" data-testid="smart-import-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#F39C12] to-[#D68910] px-4 py-2 border-b border-[#999] shrink-0 flex items-center">
        <h1 className="text-[14px] font-bold text-white flex items-center gap-2">
          <Sparkle size={16} weight="bold" />Smart Import Wizard
          <span className="text-[11px] font-normal opacity-90 ml-2">AI-powered migration into SalesCrunchCRM</span>
        </h1>
        <div className="flex-1" />
        <WatchHowLink slug="smart-import" light />
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto bg-white border border-[#CCC] shadow-sm p-6">
          <StepBar />

          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-3 py-2 mb-4 text-[12px]" data-testid="import-error">
              <WarningCircle size={14} weight="fill" className="inline mr-1" />{error}
            </div>
          )}

          {/* STEP 1 — Upload */}
          {step === 1 && (
            <section data-testid="step-upload">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-bold text-[#111]">1. Upload your contacts</h2>
                <button
                  onClick={() => { if (!showHistory) loadHistory(); setShowHistory(!showHistory); }}
                  className="text-[12px] text-[#0B5394] hover:underline font-medium"
                  data-testid="toggle-history-btn"
                >
                  {showHistory ? '× Hide history' : '📋 Import history'}
                </button>
              </div>

              {showHistory && (
                <div className="bg-[#F8F9FA] border border-[#D0D0D0] p-3 mb-4 text-[12px]" data-testid="import-history-panel">
                  <p className="font-bold text-[#333] mb-2">Past migrations</p>
                  {historyLoading ? (
                    <p className="text-[#999]">Loading...</p>
                  ) : history.length === 0 ? (
                    <p className="text-[#999] italic">No previous import sessions yet.</p>
                  ) : (
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="border-b border-[#D0D0D0]">
                          <th className="text-left py-1 px-1">When</th>
                          <th className="text-left py-1 px-1">File</th>
                          <th className="text-right py-1 px-1">Contacts</th>
                          <th className="text-right py-1 px-1">Activities</th>
                          <th className="text-right py-1 px-1">Notes</th>
                          <th className="text-right py-1 px-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map(h => (
                          <tr key={h.session_id} className="border-b border-[#E8E8E8]" data-testid={`history-row-${h.session_id}`}>
                            <td className="py-1 px-1 text-[#666]">
                              {h.created_at ? new Date(h.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                            </td>
                            <td className="py-1 px-1 truncate max-w-[200px]" title={h.filename}>{h.filename || '—'}</td>
                            <td className="py-1 px-1 text-right font-mono">{h.contacts_imported ?? 0}</td>
                            <td className="py-1 px-1 text-right font-mono">{h.activities_imported ?? 0}</td>
                            <td className="py-1 px-1 text-right font-mono">{h.notes_imported ?? 0}</td>
                            <td className="py-1 px-1 text-right">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${
                                h.status === 'completed' || h.status === 'imported' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                              }`}>{h.status || 'in progress'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              <p className="text-[13px] text-[#555] mb-4">
                Bring contacts from any CRM. We support: <strong>CSV / Excel</strong> from anywhere, <strong>ACT! .dbf</strong> files, <strong>Outlook .pst</strong>, or any export.
              </p>
              <div
                onClick={() => fileInput.current?.click()}
                className="border-2 border-dashed border-[#0B5394] rounded p-12 text-center cursor-pointer hover:bg-[#F0F4FB] transition-colors"
                data-testid="upload-dropzone"
              >
                <UploadSimple size={48} weight="bold" className="mx-auto text-[#0B5394] mb-3" />
                <div className="text-[14px] font-bold text-[#111]">Click to choose your file</div>
                <div className="text-[11px] text-[#666] mt-1">CSV, Excel (.xlsx), ACT! .dbf, or Outlook .pst · max 50MB</div>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsm,.dbf,.pst"
                  className="hidden"
                  onChange={(e) => onUpload(e.target.files?.[0])}
                  data-testid="upload-input"
                />
              </div>
              {uploading && <p className="text-[13px] text-[#0B5394] text-center mt-4 flex items-center justify-center gap-2">
                <Sparkle size={14} weight="fill" className="animate-pulse" />AI is analyzing your file...
              </p>}
            </section>
          )}

          {/* STEP 2 — Review Fields */}
          {step === 2 && session && (
            <section data-testid="step-mappings">
              <h2 className="text-lg font-bold text-[#111] mb-2">2. Review the AI's field mapping</h2>
              <p className="text-[13px] text-[#555] mb-4">
                We analyzed <strong>{session.row_count}</strong> rows from <strong>{session.filename}</strong> and matched your columns to SalesCrunchCRM fields.
                Adjust anything that looks wrong.
              </p>

              {(session.ai_warnings || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2 mb-3 text-[11px]">
                  <strong>AI warnings:</strong>
                  <ul className="list-disc pl-5 mt-1">
                    {session.ai_warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <table className="w-full border border-[#DDD] text-[12px]">
                <thead className="bg-[#F5F5F5]">
                  <tr>
                    <th className="text-left px-2 py-1.5 border-r border-[#DDD]">Source column</th>
                    <th className="text-left px-2 py-1.5 border-r border-[#DDD]">Sample</th>
                    <th className="text-left px-2 py-1.5 border-r border-[#DDD]">Maps to</th>
                    <th className="text-left px-2 py-1.5">AI confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {session.columns.map(col => {
                    const sample = (session.sample_rows || []).slice(0, 3).map(r => r[col]).filter(Boolean).join(' · ').slice(0, 60);
                    const ai = session.ai_mappings?.[col];
                    const conf = ai?.confidence || 0;
                    return (
                      <tr key={col} className="border-t border-[#DDD] hover:bg-[#FAFAFA]" data-testid={`mapping-row-${col}`}>
                        <td className="px-2 py-1.5 border-r border-[#DDD] font-medium text-[#111]">{col}</td>
                        <td className="px-2 py-1.5 border-r border-[#DDD] text-[#666] truncate max-w-[200px]">{sample || '—'}</td>
                        <td className="px-2 py-1.5 border-r border-[#DDD]">
                          <select
                            value={mappings[col] || 'skip'}
                            onChange={(e) => setMappings(m => ({ ...m, [col]: e.target.value }))}
                            className="w-full h-[26px] px-1 text-[11px] border border-[#B0B0B0] bg-white"
                            data-testid={`mapping-select-${col}`}
                          >
                            <option value="skip">— Skip this column —</option>
                            {(session.canonical_fields || []).map(f => (
                              <option key={f.id} value={f.id}>{f.id}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          {ai ? (
                            <div className="flex items-center gap-1.5">
                              <div className="w-12 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                                <div className={`h-full ${conf >= 0.85 ? 'bg-green-500' : conf >= 0.5 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${Math.round(conf * 100)}%` }} />
                              </div>
                              <span className="text-[10px] text-[#666]">{Math.round(conf * 100)}%</span>
                            </div>
                          ) : <span className="text-[10px] text-[#999]">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="flex justify-end gap-2 mt-4">
                <button onClick={reset} className="px-3 py-1.5 text-[12px] border border-[#999] bg-white hover:bg-[#F5F5F5]">Cancel</button>
                <button onClick={runPreflight} disabled={busy} className="px-4 py-1.5 text-[12px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50 flex items-center gap-1" data-testid="run-preflight-btn">
                  {busy ? 'Running...' : <>Run pre-flight check<ArrowRight size={12} weight="bold" /></>}
                </button>
              </div>
            </section>
          )}

          {/* STEP 3 — Preflight + Confirm */}
          {step === 3 && preflight && (
            <section data-testid="step-preflight">
              <h2 className="text-lg font-bold text-[#111] mb-2">3. Pre-flight check</h2>
              <p className="text-[13px] text-[#555] mb-4">Here's what we found. Confirm to import.</p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <Stat label="Total rows" value={preflight.total_rows} />
                <Stat label="Likely duplicates" value={preflight.duplicate_count || 0} warn={preflight.duplicate_count > 0} />
                <Stat label="Missing email" value={preflight.missing_email_count || 0} warn={preflight.missing_email_count > preflight.total_rows / 2} />
                <Stat label="Missing phone" value={preflight.missing_phone_count || 0} />
                <Stat label="Bad email format" value={preflight.malformed_email_count || 0} warn={preflight.malformed_email_count > 0} />
                <Stat label="Missing name" value={preflight.rows_missing_name_count || 0} warn={preflight.rows_missing_name_count > 0} />
              </div>

              <div className="bg-blue-50 border border-blue-200 text-blue-800 px-3 py-2 mb-4 text-[12px]">
                Duplicates (matched by email) will be <strong>skipped</strong> automatically. All imported contacts will be tagged <code className="bg-blue-100 px-1">import-{new Date().toISOString().substring(0, 10)}</code> so you can find them easily.
              </div>

              <div className="flex justify-between gap-2">
                <button onClick={() => setStep(2)} className="px-3 py-1.5 text-[12px] border border-[#999] bg-white hover:bg-[#F5F5F5]">← Back to mappings</button>
                <button onClick={runImport} disabled={busy} className="px-5 py-1.5 text-[12px] font-bold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center gap-1" data-testid="run-import-btn">
                  {busy ? 'Importing...' : <><Lightning size={12} weight="bold" />Import {preflight.total_rows} contacts</>}
                </button>
              </div>
            </section>
          )}

          {/* STEP 4 — Notes */}
          {step === 4 && importResult && (
            <section data-testid="step-notes">
              <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-3 mb-4 flex items-center gap-2">
                <CheckCircle size={20} weight="fill" />
                <div>
                  <div className="font-bold text-[14px]">Contacts imported!</div>
                  <div className="text-[12px]">{importResult.created} created · {importResult.skipped} skipped (duplicates or empty)</div>
                </div>
              </div>

              <h2 className="text-lg font-bold text-[#111] mb-2">4. Bring over your notes (optional)</h2>
              <p className="text-[13px] text-[#555] mb-4">
                Got a separate notes file? Upload it and our AI will match each note to the right contact — even if the note just mentions a name or company.
                This is the magic ACT! couldn't do.
              </p>

              {!notesSession ? (
                <>
                  <div
                    onClick={() => notesInput.current?.click()}
                    className="border-2 border-dashed border-[#F39C12] rounded p-8 text-center cursor-pointer hover:bg-amber-50"
                    data-testid="notes-dropzone"
                  >
                    <ChatCircleText size={36} weight="bold" className="mx-auto text-[#F39C12] mb-2" />
                    <div className="text-[13px] font-bold">Click to choose your notes file</div>
                    <div className="text-[11px] text-[#666] mt-1">CSV / Excel / TXT · one note per row</div>
                    <input
                      ref={notesInput}
                      type="file"
                      accept=".csv,.tsv,.txt,.xlsx,.xls"
                      className="hidden"
                      onChange={(e) => onNotesUpload(e.target.files?.[0])}
                      data-testid="notes-upload-input"
                    />
                  </div>
                  {uploading && <p className="text-[13px] text-[#F39C12] text-center mt-3">Uploading...</p>}
                  <div className="text-center mt-4">
                    <button onClick={skipNotes} className="text-[12px] text-[#666] hover:underline" data-testid="skip-notes-btn">
                      Skip notes — I don't have any →
                    </button>
                  </div>
                </>
              ) : !matchResult ? (
                <div className="bg-[#F8F8F8] border border-[#CCC] p-4">
                  <p className="text-[13px] mb-3">
                    📁 <strong>{notesSession.notes_filename}</strong> — found <strong>{notesSession.note_count}</strong> notes.
                    Body column auto-detected: <code className="bg-white px-1">{notesSession.body_column}</code>
                  </p>
                  <p className="text-[12px] text-[#555] mb-3">Sample of first 3:</p>
                  <ul className="space-y-1.5 mb-4">
                    {notesSession.sample_notes.slice(0, 3).map(n => (
                      <li key={n.id} className="text-[11px] text-[#666] bg-white border border-[#DDD] p-2 rounded">{n.preview}…</li>
                    ))}
                  </ul>
                  <button onClick={runMatch} disabled={busy} className="px-4 py-2 text-[12px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50 flex items-center gap-1" data-testid="run-match-btn">
                    {busy ? <><Sparkle size={12} weight="fill" className="animate-pulse" />AI matching {notesSession.note_count} notes...</> : <><Sparkle size={12} weight="bold" />Match notes to contacts with AI</>}
                  </button>
                </div>
              ) : (
                <div data-testid="match-results">
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <Stat label="High confidence" value={matchResult.high_count} good />
                    <Stat label="Medium" value={matchResult.medium_count} warn />
                    <Stat label="Low / unmatched" value={matchResult.low_count} />
                  </div>
                  <p className="text-[12px] text-[#555] mb-2">
                    High-confidence matches are pre-checked. Review and uncheck any you don't want, then import.
                  </p>
                  <div className="border border-[#CCC] max-h-[400px] overflow-y-auto">
                    {matchResult.matches.map(m => (
                      <label key={m.note_id} className="flex items-start gap-2 px-3 py-2 border-b border-[#EEE] hover:bg-[#FAFAFA] cursor-pointer" data-testid={`match-${m.note_id}`}>
                        <input type="checkbox" checked={acceptedNoteIds.has(m.note_id)} onChange={() => toggleAccept(m.note_id)} className="mt-0.5" disabled={!m.contact_id} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-[11px]">
                            <span className={`px-1.5 py-0.5 rounded font-bold ${
                              m.confidence >= 0.85 ? 'bg-green-100 text-green-700' :
                              m.confidence >= 0.5 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                            }`}>{Math.round((m.confidence || 0) * 100)}%</span>
                            <span className="font-semibold text-[#0B5394]">{m.contact_name || 'Unmatched'}</span>
                            {m.contact_company && <span className="text-[#666]">· {m.contact_company}</span>}
                          </div>
                          <div className="text-[11px] text-[#444] mt-0.5 line-clamp-2">{m.note_preview}</div>
                          <div className="text-[10px] text-[#999] italic mt-0.5">{m.reason}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-between gap-2 mt-4">
                    <button onClick={skipNotes} className="px-3 py-1.5 text-[12px] border border-[#999] bg-white hover:bg-[#F5F5F5]">Skip — finish</button>
                    <button onClick={importMatchedNotes} disabled={busy || !acceptedNoteIds.size} className="px-4 py-1.5 text-[12px] font-bold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center gap-1" data-testid="import-notes-btn">
                      {busy ? 'Importing...' : <><Lightning size={12} weight="bold" />Import {acceptedNoteIds.size} notes</>}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* STEP 5 — Activity History (calls, meetings, tasks) */}
          {step === 5 && (
            <section data-testid="step-activities">
              <h2 className="text-[16px] font-bold text-[#111] mb-1">Step 5 — Bring over your activity history</h2>
              <p className="text-[12px] text-[#666] mb-4">
                Got an export of calls, meetings, and tasks from ACT! or another CRM? Upload it here.
                We'll match each row to a contact (by email, then by name+company) and recreate the activity. <em>Optional</em> — skip if you don't have one.
              </p>

              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 mb-4 text-[12px]" data-testid="activities-tips">
                <p className="font-bold mb-1">Expected columns (we auto-detect):</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li><b>Email</b> or <b>First Name + Last Name</b> (+ optional Company) — used to match to a contact</li>
                  <li><b>Type</b> — Phone Call, Meeting, To-Do, Email, Letter, Note (auto-mapped)</li>
                  <li><b>Date</b>, <b>Time</b>, <b>Regarding/Subject</b>, <b>Priority</b>, <b>Completed</b>, <b>Notes/Details</b></li>
                </ul>
              </div>

              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setActivityFile(e.target.files?.[0] || null)}
                className="block w-full text-[12px] border border-[#999] p-2 bg-white"
                data-testid="activity-file-input"
              />
              {activityFile && (
                <p className="text-[11px] text-[#666] mt-2">
                  📎 <code className="bg-[#F0F0F0] px-1">{activityFile.name}</code> · {(activityFile.size / 1024).toFixed(1)} KB
                </p>
              )}

              <div className="flex items-center justify-between gap-2 mt-4">
                <button onClick={() => setStep(4)} className="px-3 py-1.5 text-[12px] border border-[#999] bg-white hover:bg-[#F5F5F5]" data-testid="back-to-notes-btn">← Back to notes</button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={skipActivities}
                    className="px-3 py-1.5 text-[12px] border border-[#999] bg-white hover:bg-[#F5F5F5]"
                    data-testid="skip-activities-btn"
                  >Skip — finish</button>
                  <button
                    onClick={importActivities}
                    disabled={busy || !activityFile}
                    className="px-4 py-1.5 text-[12px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50"
                    data-testid="import-activities-btn"
                  >{busy ? 'Importing...' : 'Import Activities →'}</button>
                </div>
              </div>
            </section>
          )}

          {/* STEP 6 — Relationships (B2B account hierarchies) */}
          {step === 6 && (
            <section data-testid="step-relationships">
              <h2 className="text-[16px] font-bold text-[#111] mb-1">Step 6 — Relationships</h2>
              <p className="text-[12px] text-[#666] mb-4">
                Got contact-to-contact links? B2B account hierarchies, spouses, referrers, manager/reports?
                Upload a CSV with <b>From Email</b>, <b>To Email</b>, <b>Type</b>. Skip if you don't have one.
              </p>
              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 mb-4 text-[12px]">
                <p className="font-bold mb-1">Recognized types (auto-mapped):</p>
                <p>colleague · manager · reports-to · referral · spouse · parent-company · subsidiary · vendor · client · partner · other</p>
              </div>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setRelFile(e.target.files?.[0] || null)}
                className="block w-full text-[12px] border border-[#999] p-2 bg-white"
                data-testid="rel-file-input"
              />
              {relFile && (
                <p className="text-[11px] text-[#666] mt-2">📎 {relFile.name}</p>
              )}
              <div className="flex items-center justify-between gap-2 mt-4">
                <button onClick={() => setStep(5)} className="px-3 py-1.5 text-[12px] border border-[#999] bg-white hover:bg-[#F5F5F5]">← Back</button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={skipRelationships}
                    className="px-3 py-1.5 text-[12px] border border-[#999] bg-white hover:bg-[#F5F5F5]"
                    data-testid="skip-rel-btn"
                  >Skip — next</button>
                  <button
                    onClick={importRelationships}
                    disabled={busy || !relFile}
                    className="px-4 py-1.5 text-[12px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50"
                    data-testid="import-rel-btn"
                  >{busy ? 'Importing...' : 'Import Relationships →'}</button>
                </div>
              </div>
            </section>
          )}

          {/* STEP 7 — Attachments metadata */}
          {step === 7 && (
            <section data-testid="step-attachments">
              <h2 className="text-[16px] font-bold text-[#111] mb-1">Step 7 — Attachment metadata</h2>
              <p className="text-[12px] text-[#666] mb-4">
                Got a list of files (contracts, signed agreements) that were attached to ACT! contacts? Upload a CSV with <b>Email</b> + <b>Filename</b> (+ optional Type, Size, Date). We'll create a record on each contact card so you don't lose 18 years of <i>"oh yeah, we signed that thing on..."</i> knowledge. <b>Binary files can be uploaded later.</b>
              </p>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setAttFile(e.target.files?.[0] || null)}
                className="block w-full text-[12px] border border-[#999] p-2 bg-white"
                data-testid="att-file-input"
              />
              {attFile && <p className="text-[11px] text-[#666] mt-2">📎 {attFile.name}</p>}
              <div className="flex items-center justify-between gap-2 mt-4">
                <button onClick={() => setStep(6)} className="px-3 py-1.5 text-[12px] border border-[#999] bg-white hover:bg-[#F5F5F5]">← Back</button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={skipAttachments}
                    className="px-3 py-1.5 text-[12px] border border-[#999] bg-white hover:bg-[#F5F5F5]"
                    data-testid="skip-att-btn"
                  >Skip — finish</button>
                  <button
                    onClick={importAttachments}
                    disabled={busy || !attFile}
                    className="px-4 py-1.5 text-[12px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50"
                    data-testid="import-att-btn"
                  >{busy ? 'Importing...' : 'Import Attachments →'}</button>
                </div>
              </div>
            </section>
          )}

          {/* STEP 8 — Done */}
          {step === 8 && (
            <section className="text-center py-8" data-testid="step-done">
              <CheckCircle size={64} weight="fill" className="mx-auto text-green-500 mb-4" />
              <h2 className="text-2xl font-bold text-[#111] mb-2">Migration complete! 🎉</h2>
              <div className="text-[14px] text-[#555] mb-6 space-y-1">
                <p><strong>{importResult?.created || 0}</strong> contacts imported</p>
                {notesImport && <p><strong>{notesImport.notes_created}</strong> notes attached</p>}
                {activityResult && (
                  <p>
                    <strong>{activityResult.imported}</strong> activities imported
                    {activityResult.unmatched > 0 && <span className="text-[#999]"> ({activityResult.unmatched} unmatched)</span>}
                  </p>
                )}
                {relResult && <p><strong>{relResult.imported}</strong> relationships linked</p>}
                {attResult && <p><strong>{attResult.imported}</strong> attachment records preserved</p>}
              </div>
              <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 inline-block text-[13px] mb-6">
                💡 All imported records tagged <code className="bg-blue-100 px-1">import-{new Date().toISOString().substring(0, 10)}</code>
              </div>
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => navigate('/contact')} className="px-4 py-2 text-[13px] font-bold bg-[#0B5394] text-white hover:bg-[#073763]" data-testid="go-to-contacts-btn">
                  View imported contacts →
                </button>
                <button onClick={reset} className="px-4 py-2 text-[13px] border border-[#999] bg-white hover:bg-[#F5F5F5]">
                  Run another import
                </button>
              </div>
            </section>
          )}
        </div>

        <div className="max-w-4xl mx-auto mt-4 text-center text-[11px] text-[#666]">
          💰 <strong>Saves 3+ weeks</strong> of manual data entry · <strong>$1,500+</strong> in consultant fees · works with any CRM export
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, warn, good }) {
  const color = good ? 'text-green-600' : warn ? 'text-amber-600' : 'text-[#0B5394]';
  return (
    <div className="bg-[#F8F8F8] border border-[#CCC] p-3 text-center">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-[10px] uppercase text-[#666] tracking-wide mt-0.5">{label}</div>
    </div>
  );
}
